import { useState } from 'react'
import { useExport, ExportFormat } from '../../hooks/useExport'

interface ExportButtonProps {
  documentId?: string
  documentIds?: string[]
  disabled?: boolean
  className?: string
}

export function ExportButton({ documentId, documentIds, disabled = false, className = '' }: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { exportDocument, bulkExport } = useExport()

  const isBulkExport = documentIds && documentIds.length > 0
  const isSingleExport = documentId && !isBulkExport

  const handleExport = async (format: ExportFormat) => {
    if (disabled || isExporting) return

    setIsExporting(true)
    setIsMenuOpen(false)

    try {
      if (isSingleExport) {
        await exportDocument(documentId, format)
      } else if (isBulkExport) {
        await bulkExport(documentIds, format)
      }
    } catch (error) {
      console.error('Export failed:', error)
      alert('エクスポートに失敗しました。')
    } finally {
      setIsExporting(false)
    }
  }

  const exportCount = isBulkExport ? documentIds.length : 1
  const isMultiple = exportCount > 1

  return (
    <div className="relative">
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        disabled={disabled || isExporting}
        className={`btn btn-outline btn-sm ${className}`}
        data-state={disabled || isExporting ? 'disabled' : undefined}
      >
        {isExporting ? (
          <>
            <span className="spinner sm" aria-hidden="true" />
            エクスポート中...
          </>
        ) : (
          <>
            <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            エクスポート
            {isMultiple && ` (${exportCount}件)`}
            <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {isMenuOpen && !isExporting && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Menu */}
          <div className="dropdown-menu right open" style={{ minWidth: '12rem' }}>
            <div className="dropdown-menu-label">
              {isMultiple ? `${exportCount}件の議事録を` : '議事録を'}エクスポート
            </div>

            <button
              onClick={() => handleExport('markdown')}
              className="dropdown-item"
            >
              <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div>
                <div className="font-medium">Markdown (.md)</div>
                <div className="text-xs text-tertiary">テキスト形式で軽量</div>
              </div>
            </button>

            <button
              onClick={() => handleExport('txt')}
              className="dropdown-item"
            >
              <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <div>
                <div className="font-medium">テキスト (.txt)</div>
                <div className="text-xs text-tertiary">プレーンテキスト形式</div>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  )
}